import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, CollectionItems, CollectionView} from '@bitstillery/common/components/portal'
import {generate_filters} from '@bitstillery/common/lib/filters'
import {CollectionProxy, build_transforms} from '@bitstillery/common/lib/collection'
import {$t, api} from '@bitstillery/common/app'
import {path_to_regexp} from '@bitstillery/common/lib/utils'

import type {GetOrderCollectionViewResponse} from '@bitstillery/common/api/fact2server'

import {$m, $s} from '@/app'
import {Order} from '../order/order'
import {OrderSummary} from '../order_summary/order_summary'

const filters = generate_filters({})

const transforms = build_transforms<GetOrderCollectionViewResponse[], GetOrderCollectionViewResponse>({
    with_filters_to_query: () => ({
        sort_ascending: collection.state.sort.order === 'asc' ? 'ASC' : 'DESC',
        sort_by: collection.state.sort.by,
    }),
})

const collection = new CollectionProxy<GetOrderCollectionViewResponse>()

function order_status(order: GetOrderCollectionViewResponse): {label: string; type: string} {
    if (
        [$m.order.OrderStatus.PENDING, $m.order.OrderStatus.NEW].includes(order.portal_status) &&
        order.currency === $s.identity.user.currency
    ) {
        const is_current = $s.cart.artkey !== null && String($s.cart.artkey) === String(order.artkey)
        return {label: is_current ? $t('orders.status_active') : $t('orders.status_open'), type: 'info'}
    }
    const label_map: Record<string, string> = {
        // Invoiced orders are a completed, neutral state — no alarm colour.
        [$m.order.OrderStatus.INVOICED]: 'inactive',
        [$m.order.OrderStatus.CONFIRMED]: 'success',
        [$m.order.OrderStatus.FINALIZED]: 'warning',
    }
    const type = label_map[order.portal_status] ?? 'info'
    return {label: $t(`orders.portal_status.${order.portal_status}`), type}
}

async function download_invoice(order: GetOrderCollectionViewResponse) {
    const filename = `Invoice - ${order.reference}.pdf`
    await api.download(`portal/orders/${order.artkey}/invoice`, filename)
}

async function download_delivery_note(order: GetOrderCollectionViewResponse) {
    const filename = `Packing list - ${order.reference}.pdf`
    await api.download(`portal/orders/${order.artkey}/outtake`, filename)
}

async function download_proforma(order: GetOrderCollectionViewResponse) {
    const filename = `Proforma - ${order.reference}.pdf`
    await api.download(`portal/orders/${order.artkey}/proforma`, filename)
}

function order_actions(order: GetOrderCollectionViewResponse) {
    const elements: m.Vnode[] = []
    if (order.portal_status === $m.order.OrderStatus.INVOICED) {
        elements.push(
            <Button
                icon='invoice'
                onclick={(e: Event) => {
                    e.stopPropagation()
                    void download_invoice(order)
                }}
                tip={$t('order.details.actions.download_invoice')}
                type='info'
                variant='toggle'
            />,
            <Button
                icon='delivery'
                onclick={(e: Event) => {
                    e.stopPropagation()
                    void download_delivery_note(order)
                }}
                tip={$t('order.details.actions.download_delivery_note')}
                type='info'
                variant='toggle'
            />,
        )
    } else if ([$m.order.OrderStatus.FINALIZED, $m.order.OrderStatus.CONFIRMED].includes(order.portal_status)) {
        elements.push(
            <Button
                icon='pdf'
                onclick={(e: Event) => {
                    e.stopPropagation()
                    void download_proforma(order)
                }}
                tip={$t('order.details.actions.download_proforma')}
                type='info'
                variant='toggle'
            />,
        )
    }
    return elements.length ? elements : undefined
}

function order_card(order: GetOrderCollectionViewResponse) {
    return (
        <OrderSummary
            account_slug={order.account_slug}
            actions={order_actions(order)}
            bottles={order.number_of_bottles}
            cases={order.number_of_cases}
            created_on={order.created_on}
            currency={order.currency}
            key={String(order.artkey)}
            onclick={() => m.route.set(`/my-portal/orders/${order.artkey}`)}
            reference={order.reference}
            status={order_status(order)}
            total_value={order.total_value}
        />
    )
}

/**
 * The orders list. Owns the collection lifecycle: `oninit` (re)initializes the collection
 * every time this component mounts. This matters because `CollectionItems.onremove` tears the
 * collection down (clears items, sets `state.ready = false`) when it unmounts. Keeping the list
 * as its own component means navigating to an order detail unmounts it, and navigating back mounts
 * a fresh instance whose `oninit` re-arms the collection — otherwise the list comes back empty.
 */
class OrderList extends MithrilComponent {
    override oninit() {
        // @i18n include: shell.title.my_portal_orders
        // @i18n include: shell.description.my_portal_orders
        void collection.init(
            {
                endpoint: {
                    method: 'get',
                    path: 'portal/orders/collection-view',
                },
                sort: {
                    by: 'created_on',
                    options: [
                        ['created_on', 'Created on'],
                        ['portal_status', 'Portal status'],
                        ['is_invoiced_on', 'Invoiced on'],
                    ],
                    order: 'desc',
                },
            },
            filters,
            transforms,
        )
    }

    view() {
        return (
            <div className='c-order-list'>
                <h3 className='tile-container-title'>{$t('my_portal.options.my_orders')}</h3>
                <CollectionView>
                    <CollectionItems
                        collection={collection}
                        context={this}
                        item={order_card}
                        on_row_click={(row) => {
                            void m.route.set(`/my-portal/orders/${row.artkey}`)
                        }}
                    />
                </CollectionView>
            </div>
        )
    }
}

/**
 * Dispatches between the order detail and the order list based on the route. Both branches are
 * standalone components, so crossing the list/detail boundary is a real mount/unmount and each
 * side owns its own lifecycle.
 */
export class MyPortalOrders extends MithrilComponent {
    route_matcher = path_to_regexp.match<{order_id?: string}>('/my-portal/orders{/:order_id}')

    view(_vnode: m.Vnode) {
        const route_match = this.route_matcher(m.route.get())

        if (route_match && route_match.params.order_id) {
            return <Order order_artkey={Number.parseInt(route_match.params.order_id)} />
        }

        return <OrderList />
    }
}
